import type { MaterialSrcOverride } from "building-types/materials/materialTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { StairConstruction } from "building-types/stairs/stairConstruction";
import {
  STAIR_DEFAULT_MATERIAL_OVERRIDE,
  StairBottomShape,
  StairConstructionType,
  TreadJustification,
} from "building-types/stairs/stairConstruction";
import type {
  GeneratedStairBlock,
  StairBlockRun,
  StairBlockRunResolved,
  StairConfig,
  StairExtrusion,
  StairRun,
  StairRunDeprecated,
  StairRunV2,
  StairSurface,
} from "building-types/stairs/stairTypes";
import { EdgeType, SurfaceType, StairBlockType } from "building-types/stairs/stairTypesEnum";
import { StairPlanes } from "building-types/stairs/stairTypesEnum";
import type { MaterialSrc } from "common/materials/materialSrc";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import {
  EDGE_DETECTION_EPSILON,
  TREAD_INTERSECTOR_HALF_WIDTH,
  TREAD_MIN_DEPTH,
} from "doc/elements/stairSystem/stairConstants";
import { makeXYPlane } from "doc/measurementUtils";
import type { Extrusion, Face } from "geom/brep";
import {
  faceByFourPoints,
  faceByNPoints,
  faceOrNullCopy,
  faceOrNullMoveAlongNormal,
  faceNew,
  faceOrNullReverse,
} from "geom/brep";
import { closestPointLine2 } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import { extrudeFace, extrudePolyLine2AlongNormal } from "geom/extrude";
import { ray2PolyLine2IntersectionMakeUnique } from "geom/intersect";
import type { Ray2PolyLine2Intersection } from "geom/intersectRay2";
import { intersectRay2PolyLine2 } from "geom/intersectRay2";
import { intersectRay3PlaneWithNormal } from "geom/intersectRay3";
import { line2Len, line2MidPt, line2Vec } from "geom/line2";
import { planeByThreePoints, planeCopy, planeEval, planeNew, planeNormal, planeXYAtZ } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonIntersectSimple } from "geom/polygon";
import {
  edgeIdsByPolyLine2,
  polyLine2Area,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2IsRectangle,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { ray2PerpDistance } from "geom/ray2";
import type { Vec2 } from "geom/vec2";
import { vec2Add, vec2Copy, vec2Dist, vec2Normalize, vec2Scale } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3Scale } from "geom/vec3";
import { uvGenerationForStairTread } from "../../utils/uvGenerationForEntities";
import type { StairProfile, StringerProfile } from "./profiles";
import { fullWidthProfiles, makeStringerProfile, stringers } from "./profiles";
import { isValidNewBlockPolyLine2 } from "./stairBlockValidate";
import {
  STAIR_COMPONENT_RUN_LANDING,
  STAIR_COMPONENT_SIDE,
  STAIR_COMPONENT_TREAD,
  STAIR_COMPONENT_UNDERSIDE,
} from "./stairExportComponents";

export function stairRunNew(
  stairSystemId: string,
  from: Partial<StairBlockRun> & Pick<StairBlockRun, "outline">,
): StairBlockRun {
  const { outline, id = uid() } = from;

  if (!(isValidNewBlockPolyLine2(outline))) {
    console.assert(false, "Invalid stair run outline");
  }

  const edgeIds = edgeIdsByPolyLine2(outline);

  return {
    edgeIds,
    startEdgeId: edgeIds[0],
    parentId: stairSystemId,
    treadJustification: TreadJustification.TOP,
    generated: false,
    name: "",
    sortIndex: 0,
    id,
    ...from,
    stairSystemId,
    type: StairBlockType.RUN,
    entityType: "STAIR_BLOCK",
  };
}

export function makeStairRunTopSurfaces({
  outline,
  startEdgeIndex,
  length,
  normal,
  treadDepthActual,
  treadJustification,
  bottomOfRun,
  riserHeight,
  treadCount,
  stopAtRiser,
  bottomOfStair,
  construction,
}: {
  config: StairConfig;
  outline: PolyLine2;
  startEdgeIndex: number;
  length: number;
  normal: Vec2;
  treadDepthActual: number;
  treadJustification: TreadJustification;
  riserHeight: number;
  treadCount: number;
  stopAtRiser: number | undefined;
  bottomOfStair: number;
  bottomOfRun: number;
  construction?: StairConstruction;
}): StairRunDeprecated {
  const stairSurfaces: StairSurface[] = [];

  const [sX, sY, eX, eY] = polyLine2Edge(outline, startEdgeIndex);
  const [nX, nY] = normal;
  const [unX, unY] = vec2Copy(normal);
  vec2Normalize(unX, unY);

  const planes: GeneratedStairBlock["planes"] = {};
  const bottomPlane = planeXYAtZ(bottomOfRun);
  if (!!bottomPlane) {
    planes[StairPlanes.BOTTOM] = bottomPlane;
  }

  const actualLength = treadCount * treadDepthActual;
  const [x, y] = [sX, sY];

  // whole flight is actually landing
  if (stopAtRiser !== undefined && stopAtRiser === 0) {
    const [noseX, noseY] = vec2Scale(nX, nY, 0);
    const [noseRayX, noseRayY] = vec2Add(x, y, noseX, noseY);
    const [pX, pY] = line2Vec([sX, sY, eX, eY]);

    const [backX, backY] = vec2Scale(nX, nY, length);
    const [backRayX, backRayY] = vec2Add(x, y, backX, backY);

    const stairSurface = makeStairSurface({
      outline,
      pX,
      pY,
      backRayX,
      backRayY,
      noseRayX,
      noseRayY,
      surfaceType: SurfaceType.LANDING_FROM_TREAD,
      baseElevation: bottomOfRun,
      construction,
    });

    return {
      type: "STAIR_RUN",
      stairSurfaces: [stairSurface],
      planes,
    };
  }

  let leftOverAtBottom = 0;
  switch (treadJustification) {
    case TreadJustification.BOTTOM: {
      leftOverAtBottom = 0;
      break;
    }
    case TreadJustification.CENTER: {
      leftOverAtBottom = (length - actualLength) / 2;
      break;
    }
    case TreadJustification.TOP: {
      leftOverAtBottom = length - actualLength;
      break;
    }
  }

  const isAtBottomOfRun = Math.abs(bottomOfRun - bottomOfStair) < DIST_EPSILON;

  if (treadJustification !== TreadJustification.BOTTOM && !isAtBottomOfRun && leftOverAtBottom > DIST_EPSILON) {
    const [noseX, noseY] = vec2Scale(nX, nY, 0);
    const [noseRayX, noseRayY] = vec2Add(x, y, noseX, noseY);
    const [pX, pY] = line2Vec([sX, sY, eX, eY]);

    const [backX, backY] = vec2Scale(nX, nY, leftOverAtBottom);
    const [backRayX, backRayY] = vec2Add(x, y, backX, backY);

    const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, backRayX, backRayY);

    const polygon = polygonIntersectSimple(treadIntersector, outline);
    const stairTread = polygon[0];

    const stairSurface = makeStairSurface({
      outline: stairTread,
      pX,
      pY,
      backRayX,
      backRayY,
      noseRayX,
      noseRayY,
      surfaceType: SurfaceType.LANDING_FROM_TREAD,
      baseElevation: bottomOfRun,
    });

    stairSurfaces.push(stairSurface);
  }

  let backEdgesPlane: Plane | undefined = undefined;
  for (let i = 0; i < treadCount; i++) {
    const treadHeight = riserHeight * (i + 1) + bottomOfRun;
    const [noseX, noseY] = vec2Scale(nX, nY, treadDepthActual * i + leftOverAtBottom);
    const [noseRayX, noseRayY] = vec2Add(x, y, noseX, noseY);
    const [pX, pY] = line2Vec([sX, sY, eX, eY]);
    const [upX, upY] = vec2Normalize(pX, pY);

    if (stopAtRiser !== undefined && i >= stopAtRiser - 1) {
      const [backX, backY] = vec2Scale(nX, nY, length);
      const [backRayX, backRayY] = vec2Add(x, y, backX, backY);

      const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, backRayX, backRayY);
      const polygon = polygonIntersectSimple(treadIntersector, outline);
      if (polygon?.length !== 0) {
        const stairTreadAsLanding = polygon[0];

        const stairSurface = makeStairSurface({
          outline: stairTreadAsLanding,
          pX,
          pY,
          backRayX,
          backRayY,
          noseRayX,
          noseRayY,
          surfaceType: SurfaceType.LANDING_FROM_TREAD,
          baseElevation: treadHeight,
        });

        stairSurfaces.push(stairSurface);

        return {
          type: "STAIR_RUN",
          stairSurfaces: stairSurfaces,
          planes: {
            ...planes,
            ...(!!backEdgesPlane ? { [StairPlanes.BACK]: planeCopy(backEdgesPlane) } : {}),
          },
        };
      }
    }

    const noseInts = intersectRay2PolyLine2(outline, noseRayX, noseRayY, pX, pY);

    let revertToPolygonIntersection = false;

    if (noseInts.length < 2) {
      revertToPolygonIntersection = true;
    }

    let firstNoseInt: Ray2PolyLine2Intersection | undefined;
    let lastNoseInt: Ray2PolyLine2Intersection | undefined;

    if (!revertToPolygonIntersection) {
      ray2PolyLine2IntersectionMakeUnique(noseInts);

      noseInts.sort((a, b) => a.t - b.t);

      firstNoseInt = noseInts[0];
      lastNoseInt = noseInts[noseInts.length - 1];
    }

    const [backX, backY] = vec2Scale(nX, nY, treadDepthActual * (i + 1) + leftOverAtBottom);
    const [backRayX, backRayY] = vec2Add(x, y, backX, backY);

    let firstbackInt: Ray2PolyLine2Intersection | undefined;
    let lastbackInt: Ray2PolyLine2Intersection | undefined;

    if (!revertToPolygonIntersection) {
      const backInts = intersectRay2PolyLine2(outline, backRayX, backRayY, pX, pY);

      if (backInts.length < 2) {
        revertToPolygonIntersection = true;
      }

      if (!revertToPolygonIntersection) {
        ray2PolyLine2IntersectionMakeUnique(backInts);

        backInts.sort((a, b) => a.t - b.t);

        firstbackInt = backInts[0];
        lastbackInt = backInts[noseInts.length - 1];
      }
    }

    let stairTread: PolyLine2;

    // revert to polygon intersection if edge intersection at nose and back not the same
    if (
      revertToPolygonIntersection ||
      !firstNoseInt ||
      !lastNoseInt ||
      !firstbackInt ||
      !lastbackInt ||
      Math.abs(firstNoseInt.t - firstbackInt.t) > DIST_EPSILON ||
      Math.abs(lastNoseInt.t - lastbackInt.t) > DIST_EPSILON
    ) {
      const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, backRayX, backRayY);
      const polygon = polygonIntersectSimple(treadIntersector, outline);
      stairTread = polygon[0];
    } else {
      stairTread = polyLine2New([
        firstNoseInt.x,
        firstNoseInt.y,
        firstbackInt.x,
        firstbackInt.y,
        lastbackInt.x,
        lastbackInt.y,
        lastNoseInt.x,
        lastNoseInt.y,
        firstNoseInt.x,
        firstNoseInt.y,
      ]);

      if (polyLine2Area(stairTread) < DIST_EPSILON) {
        const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, backRayX, backRayY);
        const polygon = polygonIntersectSimple(treadIntersector, outline);
        stairTread = polygon[0];
      }
    }

    if (stairTread) {
      const stairSurface = makeStairSurface({
        outline: stairTread,
        pX,
        pY,
        backRayX,
        backRayY,
        noseRayX,
        noseRayY,
        surfaceType: SurfaceType.TREAD,
        baseElevation: treadHeight,
        construction,
      });

      if (backEdgesPlane === undefined || backEdgesPlane === null) {
        backEdgesPlane = planeNew(
          backRayX,
          backRayY,
          treadHeight,
          upX,
          upY,
          0,
          unX * treadDepthActual,
          unY * treadDepthActual,
          riserHeight,
        );
      }

      stairSurfaces.push(stairSurface);
    }
  }

  const leftOverAtTop = length - treadDepthActual * treadCount + leftOverAtBottom;

  if (
    leftOverAtTop > DIST_EPSILON &&
    (treadJustification === TreadJustification.BOTTOM || treadJustification === TreadJustification.CENTER)
  ) {
    const treadHeight = riserHeight * (treadCount + 1) + bottomOfRun;
    const [noseX, noseY] = vec2Scale(nX, nY, treadDepthActual * treadCount + leftOverAtBottom);
    const [noseRayX, noseRayY] = vec2Add(x, y, noseX, noseY);
    const [pX, pY] = line2Vec([sX, sY, eX, eY]);
    const [backX, backY] = vec2Scale(nX, nY, length);
    const [backRayX, backRayY] = vec2Add(x, y, backX, backY);

    const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, backRayX, backRayY);

    const polygon = polygonIntersectSimple(treadIntersector, outline);
    const stairTreadAsLanding = polygon[0];

    if (stairTreadAsLanding) {
      const stairSurface = makeStairSurface({
        outline: stairTreadAsLanding,
        pX,
        pY,
        backRayX,
        backRayY,
        noseRayX,
        noseRayY,
        surfaceType: SurfaceType.LANDING_FROM_TREAD,
        baseElevation: treadHeight,
      });
      planes[StairPlanes.LANDING] = planeXYAtZ(treadHeight) ?? undefined;

      stairSurfaces.push(stairSurface);
    }
  }

  return {
    type: "STAIR_RUN",
    stairSurfaces,
    planes: {
      ...planes,
      [StairPlanes.BACK]: backEdgesPlane,
    },
  };
}

export function makeTreadIntersector(
  noseRayX: number,
  noseRayY: number,
  pX: number,
  pY: number,
  backRayX: number,
  backRayY: number,
) {
  return polyLine2New([
    noseRayX + pX * -TREAD_INTERSECTOR_HALF_WIDTH,
    noseRayY + pY * -TREAD_INTERSECTOR_HALF_WIDTH,
    backRayX + pX * -TREAD_INTERSECTOR_HALF_WIDTH,
    backRayY + pY * -TREAD_INTERSECTOR_HALF_WIDTH,
    backRayX + pX * TREAD_INTERSECTOR_HALF_WIDTH,
    backRayY + pY * TREAD_INTERSECTOR_HALF_WIDTH,
    noseRayX + pX * TREAD_INTERSECTOR_HALF_WIDTH,
    noseRayY + pY * TREAD_INTERSECTOR_HALF_WIDTH,
    noseRayX + pX * -TREAD_INTERSECTOR_HALF_WIDTH,
    noseRayY + pY * -TREAD_INTERSECTOR_HALF_WIDTH,
  ]);
}

interface RunLengthAndNormal {
  readonly length: number;
  readonly normal: Vec2 | undefined;
}

export function getStairRunLengthAndNormal(outline: PolyLine2, startEdgeIndex: number): RunLengthAndNormal {
  const [sX, sY, eX, eY] = polyLine2Edge(outline, startEdgeIndex);

  let maxDistFromStartEdge = -1;
  let unitPerpVecToMaxDist: Vec2 | undefined;
  for (let i = 0, l = outline.length; i < l; i += 2) {
    const x = outline[i];
    const y = outline[i + 1];

    const [cX, cY] = closestPointLine2(sX, sY, eX, eY, x, y, false);
    const dist = vec2Dist(x, y, cX, cY);

    if (dist > maxDistFromStartEdge) {
      maxDistFromStartEdge = dist;
      const [vX, vY] = line2Vec([cX, cY, x, y]);
      const [nvX, nvY] = vec2Normalize(vX, vY);
      unitPerpVecToMaxDist = [nvX, nvY];
    }
  }

  return {
    length: maxDistFromStartEdge,
    normal: unitPerpVecToMaxDist,
  };
}

interface MakeStairSurfaceInfo {
  outline: PolyLine2;
  pX: number;
  pY: number;
  noseRayX: number;
  noseRayY: number;
  backRayX: number;
  backRayY: number;
  surfaceType: SurfaceType;
  baseElevation: number;
  nosing?: number;
  construction?: StairConstruction;
}

export function makeStairSurface({
  outline,
  pX,
  pY,
  noseRayX,
  noseRayY,
  backRayX,
  backRayY,
  surfaceType,
  baseElevation,
  nosing,
  construction,
}: MakeStairSurfaceInfo): StairSurface {
  const pl = polyLine2Copy(outline);
  polyLine2SetCCW(pl);
  let firstLabeledEdge = -1;
  let firstLabelType: EdgeType | undefined;

  for (let i = 0, l = pl.length - 2; i < l; i += 2) {
    const wrappedI = i % pl.length;

    const x1 = pl[wrappedI];
    const y1 = pl[wrappedI + 1];
    const x2 = pl[wrappedI + 2];
    const y2 = pl[wrappedI + 3];

    const [cX, cY] = line2MidPt(x1, y1, x2, y2);

    const distFromBack = ray2PerpDistance(backRayX, backRayY, pX, pY, cX, cY);

    if (distFromBack < EDGE_DETECTION_EPSILON) {
      firstLabeledEdge = wrappedI / 2;
      firstLabelType = EdgeType.BACK;
      break;
    }

    const distFromFront = ray2PerpDistance(noseRayX, noseRayY, pX, pY, cX, cY);

    if (distFromFront < EDGE_DETECTION_EPSILON) {
      firstLabeledEdge = wrappedI / 2;
      firstLabelType = EdgeType.FRONT;
      break;
    }
  }

  const edgeTypeMap: { [index: number]: EdgeType | undefined } = [];

  //place first edge type
  edgeTypeMap[firstLabeledEdge] = firstLabelType;

  let lastEdgeType = firstLabelType;
  for (let i = 0, l = pl.length - 4; i < l; i += 2) {
    const rawIndex = i + firstLabeledEdge * 2 + 2;
    const adjustedIndex = rawIndex % (pl.length - 2);

    const x1 = pl[adjustedIndex];
    const y1 = pl[adjustedIndex + 1];
    const x2 = pl[adjustedIndex + 2];
    const y2 = pl[adjustedIndex + 3];

    const [cX, cY] = line2MidPt(x1, y1, x2, y2);

    const distFromBack = ray2PerpDistance(backRayX, backRayY, pX, pY, cX, cY);

    if (distFromBack < EDGE_DETECTION_EPSILON) {
      lastEdgeType = EdgeType.BACK;
      edgeTypeMap[adjustedIndex / 2] = EdgeType.BACK;
      continue;
    }

    const distFromFront = ray2PerpDistance(noseRayX, noseRayY, pX, pY, cX, cY);

    if (distFromFront < EDGE_DETECTION_EPSILON) {
      lastEdgeType = EdgeType.FRONT;
      edgeTypeMap[adjustedIndex / 2] = EdgeType.FRONT;
      continue;
    }

    if (lastEdgeType === EdgeType.FRONT) {
      edgeTypeMap[adjustedIndex / 2] = EdgeType.RIGHT;
      continue;
    }

    if (lastEdgeType === EdgeType.BACK) {
      edgeTypeMap[adjustedIndex / 2] = EdgeType.LEFT;
    }
  }

  const edgeTypes: (EdgeType | undefined)[] = [];
  for (let i = 0, l = Object.keys(edgeTypeMap).length; i < l; i++) {
    edgeTypes.push(edgeTypeMap[i]);
  }

  let material: MaterialSrc | undefined;
  if (construction?.type === StairConstructionType.GENERIC) {
    if (construction.hasTreadMaterial) {
      material = construction.treadMaterial;
    }
  }

  return {
    type: surfaceType,
    outline: polyLine2Copy(pl),
    edgeTypes,
    zHeight: baseElevation,
    nosing,
    material,
  };
}

export function addStairRunExtrusions(stairRun: StairRun, blockResult: GeneratedStairBlock, riserHeightActual: number) {
  const wallTrimFaces = blockResult.wallTrimFaces;
  if (stairRun.type === "STAIR_RUN") {
    const { stairSurfaces, planes } = stairRun;
    const extrusions: Extrusion[] = [];

    const backEdgesPlane = planes[StairPlanes.BACK];
    if (!backEdgesPlane) {
      for (const { type, zHeight, outline, material } of stairSurfaces) {
        if (type === SurfaceType.LANDING_FROM_TREAD) {
          const plane = makeXYPlane(0, 0, zHeight);
          blockResult.stairPlanPolys.push({ outline, height: zHeight, outlinesPDF: [], overrideOutlineForPDF: false });
          addWallTrimFaceIfDefined(
            wallTrimFaces,
            faceOrNullMoveAlongNormal(faceNew([outline], planeCopy(plane)), -riserHeightActual),
          );
          blockResult.geom.add(
            extrudePolyLine2AlongNormal(outline, plane, -riserHeightActual),
            materialWithStairComponent(
              material
                ? {
                    materialSrc: material,
                    uvGen: uvGenerationForStairTread(),
                  }
                : STAIR_DEFAULT_MATERIAL_OVERRIDE,
              STAIR_COMPONENT_RUN_LANDING,
            ),
          );
        }
      }
    } else {
      const { originX, originY, originZ } = backEdgesPlane;
      const [nX, nY, nZ] = planeNormal(backEdgesPlane);

      for (const { type, zHeight, outline, edgeTypes, nosing, material: stairMaterial } of stairSurfaces) {
        const material = stairMaterial
          ? {
              materialSrc: stairMaterial,
              uvGen: uvGenerationForStairTread(),
            }
          : STAIR_DEFAULT_MATERIAL_OVERRIDE;

        if (type !== SurfaceType.TREAD) {
          const plane = makeXYPlane(0, 0, zHeight);
          const landingPlane = planeXYAtZ(zHeight - riserHeightActual);
          if (!!landingPlane) {
            blockResult.planes[StairPlanes.LANDING] = landingPlane;
          }

          blockResult.stairPlanPolys.push({
            outline,
            height: zHeight,
            outlinesPDF: [],
            overrideOutlineForPDF: false,
            edgeTypes,
            nosing,
          });

          addWallTrimFaceIfDefined(
            wallTrimFaces,
            faceOrNullMoveAlongNormal(faceNew([polyLine2Copy(outline)], planeCopy(plane)), -riserHeightActual),
          );

          extrusions.push(extrudePolyLine2AlongNormal(outline, plane, -riserHeightActual));

          continue;
        }

        const plane = makeXYPlane(0, 0, zHeight);
        const topFace = faceNew([polyLine2SetCW(polyLine2Copy(outline))], planeCopy(plane));
        blockResult.geom.add(topFace, materialWithStairComponent(material, STAIR_COMPONENT_TREAD));

        const bottomCap: number[] = [];

        for (const indexAsString of Object.keys(edgeTypes)) {
          const index = +indexAsString;
          const edgeType = edgeTypes[index];

          const x1 = outline[index * 2];
          const y1 = outline[index * 2 + 1];
          const z1 = zHeight;

          const x2 = outline[index * 2 + 2];
          const y2 = outline[index * 2 + 3];
          const z2 = zHeight;

          const intersection1 = intersectRay3PlaneWithNormal(
            x1,
            y1,
            z1,
            0,
            0,
            -1,
            originX,
            originY,
            originZ,
            nX,
            nY,
            nZ,
          );
          assertIsNotNullOrUndefined(intersection1);
          const [intX1, intY1, intZ1] = intersection1;

          const intersection2 = intersectRay3PlaneWithNormal(
            x2,
            y2,
            z2,
            0,
            0,
            -1,
            originX,
            originY,
            originZ,
            nX,
            nY,
            nZ,
          );
          assertIsNotNullOrUndefined(intersection2);
          const [intX2, intY2, intZ2] = intersection2;

          bottomCap.push(intX1);
          bottomCap.push(intY1);
          bottomCap.push(intZ1);

          if (edgeType === EdgeType.BACK) {
            continue;
          }

          if (edgeType === EdgeType.FRONT) {
            const stairEdgeFace = faceByFourPoints(x1, y1, z1, intX1, intY1, intZ1, intX2, intY2, intZ2, x2, y2, z2);
            if (stairEdgeFace) {
              blockResult.geom.add(
                stairEdgeFace,
                materialWithStairComponent(STAIR_DEFAULT_MATERIAL_OVERRIDE, STAIR_COMPONENT_SIDE),
              );
            }
            continue;
          }

          if (edgeType === EdgeType.RIGHT) {
            const stairEdgeFace = faceByFourPoints(x1, y1, z1, intX1, intY1, intZ1, intX2, intY2, intZ2, x2, y2, z2);
            if (stairEdgeFace) {
              blockResult.geom.add(
                stairEdgeFace,
                materialWithStairComponent(STAIR_DEFAULT_MATERIAL_OVERRIDE, STAIR_COMPONENT_SIDE),
              );
            }
            continue;
          }

          if (edgeType === EdgeType.LEFT) {
            const stairEdgeFace = faceByFourPoints(x2, y2, z2, intX2, intY2, intZ2, intX1, intY1, intZ1, x1, y1, z1);
            if (stairEdgeFace) {
              const reversedFace = !!stairEdgeFace ? faceOrNullReverse(stairEdgeFace) : undefined;
              if (reversedFace) {
                blockResult.geom.add(
                  reversedFace,
                  materialWithStairComponent(STAIR_DEFAULT_MATERIAL_OVERRIDE, STAIR_COMPONENT_SIDE),
                );
              }
            }
          }
        }

        blockResult.stairPlanPolys.push({
          outline: outline,
          height: zHeight,
          outlinesPDF: [],
          overrideOutlineForPDF: false,
          edgeTypes,
          nosing,
        });

        if (bottomCap.length < 9) {
          console.warn("dropped stair bottom face");
          continue;
        }

        const bottomCapFace = faceByNPoints(bottomCap);

        if (!bottomCapFace || bottomCapFace.loops.length === 0) {
          continue;
        }

        const norm = [...planeNormal(bottomCapFace.plane)];

        if (blockResult.planes[StairPlanes.UNDERSIDE] === undefined && Math.abs(norm[2]) !== 1) {
          blockResult.planes[StairPlanes.UNDERSIDE] = bottomCapFace.plane;
        }

        const stairRunBottomFace = norm[2] < 0 ? bottomCapFace : faceOrNullReverse(bottomCapFace);
        addWallTrimFaceIfDefined(wallTrimFaces, !!stairRunBottomFace ? faceOrNullCopy(stairRunBottomFace) : null);
        if (!!stairRunBottomFace) {
          blockResult.geom.add(
            stairRunBottomFace,
            materialWithStairComponent(STAIR_DEFAULT_MATERIAL_OVERRIDE, STAIR_COMPONENT_UNDERSIDE),
          );
        }
      }

      blockResult.geom.addMany(
        extrusions,
        materialWithStairComponent(STAIR_DEFAULT_MATERIAL_OVERRIDE, STAIR_COMPONENT_RUN_LANDING),
      );
    }
  } else if (stairRun.type === "STAIR_RUN_V2") {
    const { stairSurfaces, planes, stairExtrusions: extrusions } = stairRun;

    const backEdgesPlane = planes[StairPlanes.BACK];
    if (!backEdgesPlane) {
      for (const { type, zHeight, outline } of stairSurfaces) {
        if (type === SurfaceType.LANDING_FROM_TREAD) {
          const plane = makeXYPlane(0, 0, zHeight);
          blockResult.stairPlanPolys.push({ outline, height: zHeight, outlinesPDF: [], overrideOutlineForPDF: false });
          addWallTrimFaceIfDefined(
            wallTrimFaces,
            faceOrNullMoveAlongNormal(faceNew([outline], planeCopy(plane)), -riserHeightActual),
          );
        }
      }
    } else {
      const { originX, originY, originZ } = backEdgesPlane;
      const [nX, nY, nZ] = planeNormal(backEdgesPlane);

      for (const { type, zHeight, outline, edgeTypes, nosing } of stairSurfaces) {
        if (type !== SurfaceType.TREAD) {
          const plane = makeXYPlane(0, 0, zHeight);

          blockResult.stairPlanPolys.push({
            outline,
            height: zHeight,
            outlinesPDF: [],
            overrideOutlineForPDF: false,
            edgeTypes,
            nosing,
          });

          addWallTrimFaceIfDefined(
            wallTrimFaces,
            faceOrNullMoveAlongNormal(faceNew([polyLine2Copy(outline)], planeCopy(plane)), -riserHeightActual),
          );
          continue;
        }

        const bottomCap: number[] = [];

        for (const indexAsString of Object.keys(edgeTypes)) {
          const index = +indexAsString;
          const edgeType = edgeTypes[index];

          const x1 = outline[index * 2];
          const y1 = outline[index * 2 + 1];
          const z1 = zHeight;

          const intersection1 = intersectRay3PlaneWithNormal(
            x1,
            y1,
            z1,
            0,
            0,
            -1,
            originX,
            originY,
            originZ,
            nX,
            nY,
            nZ,
          );
          assertIsNotNullOrUndefined(intersection1);
          const [intX1, intY1, intZ1] = intersection1;

          bottomCap.push(intX1);
          bottomCap.push(intY1);
          bottomCap.push(intZ1);

          if (edgeType === EdgeType.BACK) {
            continue;
          }
        }

        blockResult.stairPlanPolys.push({
          outline: outline,
          height: zHeight,
          outlinesPDF: [],
          overrideOutlineForPDF: false,
          edgeTypes,
          nosing,
        });

        if (bottomCap.length < 9) {
          console.warn("dropped stair bottom face");
          continue;
        }

        const bottomCapFace = faceByNPoints(bottomCap);
        const stairRunBottomFace = faceOrNullReverse(bottomCapFace);
        addWallTrimFaceIfDefined(wallTrimFaces, stairRunBottomFace ? faceOrNullCopy(stairRunBottomFace) : null);
      }

      const extrusionsByMaterial = extrusions.reduce(
        (acc, { extrusion, material = STAIR_DEFAULT_MATERIAL_OVERRIDE }) => {
          if (!material) {
            return acc;
          }

          const { materialSrc } = material;
          if (!materialSrc) {
            return acc;
          }

          const { templateId } = materialSrc;
          if (templateId === undefined) {
            return acc;
          }

          if (!acc[templateId]) {
            acc[templateId] = [material, []];
          }

          acc[templateId][1].push(extrusion);

          return acc;
        },
        {} as { [key: number]: [MaterialSrcOverride, Extrusion[]] },
      );

      for (const materialId in extrusionsByMaterial) {
        const [material, extrusions] = extrusionsByMaterial[materialId];

        blockResult.geom.addMany(extrusions, material);
      }
    }
  }
}

function addWallTrimFaceIfDefined(wallTrimFaces: Face[], wallTrimFace: Face | null | undefined) {
  if (!!wallTrimFace) {
    wallTrimFaces.push(wallTrimFace);
  }
}

function materialWithStairComponent(material: MaterialSrcOverride, exportComponentName: string): MaterialSrcOverride {
  return {
    ...material,
    exportComponentName,
  };
}

/**
 * ```
 *   [backX,backY]
 *   x     +---------------+ -------------/-
 *   .     |               |              |
 *   .     |               |              |length
 *   .     |               |              |
 *   .     |- - - - - - - -| --------/-   |
 *   .     |               |         |    |
 *   .     |- - - - - - - -|         |actualLength
 *   .     :               :         :    :
 *
 *   .     :               :         :    :
 *   .     |- - - - - - - -|         |    |
 *   norm. |               |         |    |
 *   ^     |- - - - - - - -|  --/-   |    |
 *   |     |               |    |treadDepthActual
 *   +     +---------------+  --/----/----/-
 *          startEdgeIndex
 * ```
 */
interface MakeStairRunConfig {
  block: StairBlockRunResolved;
  config: StairConfig;
  outline: PolyLine2;
  startEdgeIndex: number;
  length: number;
  /**
   * normal to start edge
   */
  normal: Vec2;
  treadDepthActual: number;
  treadJustification: TreadJustification;
  riserHeight: number;
  treadCount: number;
  stopAtRiser: number | undefined;
  /**
   * z height of bottom of stair system
   */
  bottomOfStair: number;
  /**
   * z height of bottom of this stair run
   */
  bottomOfRun: number;
  construction?: StairConstruction;
  issues: BuildingGenerationIssue[];
}

export function makeStairRunGeom({
  block,
  outline,
  startEdgeIndex,
  length,
  normal,
  treadDepthActual,
  treadJustification,
  bottomOfRun,
  riserHeight,
  treadCount,
  stopAtRiser,
  bottomOfStair,
  construction,
  issues,
}: MakeStairRunConfig): StairRunV2 {
  const stairSurfaces: StairSurface[] = [];
  const stairExtrusions: StairExtrusion[] = [];
  const treadPoints: Array<number> = [];
  let nosing;
  if (construction) {
    nosing = construction.nosing;
  }

  if (construction?.type === StairConstructionType.MONOLITHIC) {
    const { hasTreadMaterial } = construction;
    if (!hasTreadMaterial) {
      nosing = undefined;
    }
  }

  // all of these points are in the XY plane
  const startEdge = polyLine2Edge(outline, startEdgeIndex);
  const [startX, startY, endX, endY] = startEdge;
  const [normalX, normalY] = normal;
  const [unX, unY] = vec2Copy(normal);
  vec2Normalize(unX, unY);

  const actualLength = treadCount * treadDepthActual;

  if (actualLength < DIST_EPSILON) {
    issues.push({
      type: BuildingGenerationIssueType.STAIR_GENERATION_ISSUE,
      message: "stair run has zero length",
      handle: { type: "STAIR_BLOCK", id: block.id },
    });
    return {
      type: "STAIR_RUN_V2",
      stairSurfaces,
      stairExtrusions,
      planes: {},
    };
  }

  const [pX, pY] = line2Vec([startX, startY, endX, endY]);
  const [upX, upY] = vec2Normalize(pX, pY);

  const stringerPlane =
    planeByThreePoints(startX, startY, 0, startX, startY, 1, startX + normalX, startY + normalY, 0) ?? undefined;

  const [backX, backY] = vec2Scale(normalX, normalY, length);
  const [backRayX, backRayY] = vec2Add(startX, startY, backX, backY);

  // whole flight is actually landing
  if (stopAtRiser !== undefined && stopAtRiser === 0) {
    const [noseX, noseY] = vec2Scale(normalX, normalY, 0);
    const [noseRayX, noseRayY] = vec2Add(startX, startY, noseX, noseY);
    const [pX, pY] = line2Vec([startX, startY, endX, endY]);

    const stairSurface = makeStairSurface({
      outline,
      pX,
      pY,
      backRayX,
      backRayY,
      noseRayX,
      noseRayY,
      surfaceType: SurfaceType.LANDING_FROM_TREAD,
      baseElevation: bottomOfRun,
      nosing,
      construction,
    });

    return {
      type: "STAIR_RUN_V2",
      stairSurfaces: [stairSurface],
      stairExtrusions,
      planes: stairBlockPlanes(
        bottomOfRun,
        stringerPlane,
        makeStringerProfile({
          runBottomX: bottomOfRun,
          treadPoints,
        }),
        construction,
      ),
    };
  }

  let leftOverAtBottom = 0;
  switch (treadJustification) {
    case TreadJustification.BOTTOM: {
      leftOverAtBottom = 0;
      break;
    }
    case TreadJustification.CENTER: {
      leftOverAtBottom = (length - actualLength) / 2;
      break;
    }
    case TreadJustification.TOP: {
      leftOverAtBottom = length - actualLength;
      break;
    }
  }

  const isAtBottomOfRun = Math.abs(bottomOfRun - bottomOfStair) < DIST_EPSILON;

  if (treadJustification !== TreadJustification.BOTTOM && !isAtBottomOfRun && leftOverAtBottom > DIST_EPSILON) {
    const [noseX, noseY] = vec2Scale(normalX, normalY, 0);
    const [noseRayX, noseRayY] = vec2Add(startX, startY, noseX, noseY);
    const [pX, pY] = line2Vec([startX, startY, endX, endY]);

    const [leftoverBackX, leftoverBackY] = vec2Scale(normalX, normalY, leftOverAtBottom);
    const [leftoverBackRayX, leftoverBackRayY] = vec2Add(startX, startY, leftoverBackX, leftoverBackY);

    const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, leftoverBackRayX, leftoverBackRayY);

    const polygon = polygonIntersectSimple(treadIntersector, outline);
    const stairTread = polygon[0];

    const stairSurface = makeStairSurface({
      outline: stairTread,
      pX,
      pY,
      backRayX: leftoverBackRayX,
      backRayY: leftoverBackRayY,
      noseRayX,
      noseRayY,
      surfaceType: SurfaceType.LANDING_FROM_TREAD,
      baseElevation: bottomOfRun,
      nosing,
      construction,
    });

    treadPoints.push(bottomOfRun, 0, bottomOfRun, leftOverAtBottom);

    stairSurfaces.push(stairSurface);
  }

  let backEdgesPlane: Plane | undefined = undefined;

  for (let i = 0; i < treadCount; i++) {
    const treadHeight = riserHeight * (i + 1) + bottomOfRun;
    const noseDepth = treadDepthActual * i + leftOverAtBottom;
    const [noseX, noseY] = vec2Scale(normalX, normalY, noseDepth);
    const [noseRayX, noseRayY] = vec2Add(startX, startY, noseX, noseY);

    if (stopAtRiser !== undefined && i >= stopAtRiser - 1) {
      const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, backRayX, backRayY);
      const polygon = polygonIntersectSimple(treadIntersector, outline);
      if (polygon?.length !== 0) {
        const stairTreadAsLanding = polygon[0];

        const stairSurface = makeStairSurface({
          outline: stairTreadAsLanding,
          pX,
          pY,
          backRayX,
          backRayY,
          noseRayX,
          noseRayY,
          surfaceType: SurfaceType.LANDING_FROM_TREAD,
          baseElevation: treadHeight,
          nosing,
          construction,
        });

        stairSurfaces.push(stairSurface);

        return {
          type: "STAIR_RUN_V2",
          stairSurfaces,
          planes: {
            ...(backEdgesPlane ? { [StairPlanes.BACK]: planeCopy(backEdgesPlane) } : {}),
            ...stairBlockPlanes(
              bottomOfRun,
              stringerPlane,
              makeStringerProfile({
                treadPoints,
                runBottomX: bottomOfRun,
                stringerMinDepth: construction?.hasOwnProperty("stringerMinDepth")
                  ? construction["stringerMinDepth"]
                  : undefined,
                riserShelf: !!construction?.hasRisers,
              }),
              construction,
            ),
          },
          stairExtrusions: [
            ...stringers(outline, startEdgeIndex, stringerPlane, treadPoints, bottomOfRun, construction),
            ...fullWidthExtrusions(
              outline,
              startEdgeIndex,
              stringerPlane,
              fullWidthProfiles(treadPoints, bottomOfRun, construction),
            ),
          ],
        };
      }
    }

    treadPoints.push(treadHeight, noseDepth, treadHeight, noseDepth + treadDepthActual);

    const noseInts = intersectRay2PolyLine2(outline, noseRayX, noseRayY, pX, pY);

    let revertToPolygonIntersection = false;

    if (noseInts.length < 2) {
      revertToPolygonIntersection = true;
    }

    let firstNoseInt: Ray2PolyLine2Intersection | undefined;
    let lastNoseInt: Ray2PolyLine2Intersection | undefined;

    if (!revertToPolygonIntersection) {
      ray2PolyLine2IntersectionMakeUnique(noseInts);

      noseInts.sort((a, b) => a.t - b.t);

      firstNoseInt = noseInts[0];
      lastNoseInt = noseInts[noseInts.length - 1];
    }

    const treadNoseDepth = treadDepthActual * (i + 1) + leftOverAtBottom;
    const [treadBackX, treadBackY] = vec2Scale(normalX, normalY, treadNoseDepth);
    const [treadBackRayX, treadBackRayY] = vec2Add(startX, startY, treadBackX, treadBackY);

    let firstbackInt: Ray2PolyLine2Intersection | undefined;
    let lastbackInt: Ray2PolyLine2Intersection | undefined;

    if (!revertToPolygonIntersection) {
      const backInts = intersectRay2PolyLine2(outline, treadBackRayX, treadBackRayY, pX, pY);

      if (backInts.length < 2) {
        revertToPolygonIntersection = true;
      }

      if (!revertToPolygonIntersection) {
        ray2PolyLine2IntersectionMakeUnique(backInts);

        backInts.sort((a, b) => a.t - b.t);

        firstbackInt = backInts[0];
        lastbackInt = backInts[noseInts.length - 1];
      }
    }

    let stairTread: PolyLine2;

    // revert to polygon intersection if edge intersection at nose and back not the same
    if (
      revertToPolygonIntersection ||
      !firstNoseInt ||
      !lastNoseInt ||
      !firstbackInt ||
      !lastbackInt ||
      Math.abs(firstNoseInt.t - firstbackInt.t) > DIST_EPSILON ||
      Math.abs(lastNoseInt.t - lastbackInt.t) > DIST_EPSILON
    ) {
      const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, treadBackRayX, treadBackRayY);
      const polygon = polygonIntersectSimple(treadIntersector, outline);
      stairTread = polygon[0];
    } else {
      stairTread = polyLine2New([
        firstNoseInt.x,
        firstNoseInt.y,
        firstbackInt.x,
        firstbackInt.y,
        lastbackInt.x,
        lastbackInt.y,
        lastNoseInt.x,
        lastNoseInt.y,
        firstNoseInt.x,
        firstNoseInt.y,
      ]);

      if (polyLine2Area(stairTread) < DIST_EPSILON) {
        const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, treadBackRayX, treadBackRayY);
        const polygon = polygonIntersectSimple(treadIntersector, outline);
        stairTread = polygon[0];
      }
    }

    if (stairTread) {
      const stairSurface = makeStairSurface({
        outline: stairTread,
        pX,
        pY,
        backRayX: treadBackRayX,
        backRayY: treadBackRayY,
        noseRayX,
        noseRayY,
        surfaceType: SurfaceType.TREAD,
        baseElevation: treadHeight,
        nosing,
        construction,
      });

      if (backEdgesPlane === undefined || backEdgesPlane === null) {
        backEdgesPlane = planeNew(
          treadBackRayX,
          treadBackRayY,
          treadHeight,
          upX,
          upY,
          0,
          unX * treadDepthActual,
          unY * treadDepthActual,
          riserHeight,
        );
      }

      stairSurfaces.push(stairSurface);
    }
  }

  const leftOverAtTop = length - treadDepthActual * treadCount + leftOverAtBottom;

  if (
    leftOverAtTop > DIST_EPSILON &&
    (treadJustification === TreadJustification.BOTTOM || treadJustification === TreadJustification.CENTER)
  ) {
    const treadHeight = riserHeight * (treadCount + 1) + bottomOfRun;
    const [noseX, noseY] = vec2Scale(normalX, normalY, treadDepthActual * treadCount + leftOverAtBottom);
    const [noseRayX, noseRayY] = vec2Add(startX, startY, noseX, noseY);

    const treadIntersector = makeTreadIntersector(noseRayX, noseRayY, pX, pY, backRayX, backRayY);

    const polygon = polygonIntersectSimple(treadIntersector, outline);
    const stairTreadAsLanding = polygon[0];

    if (leftOverAtTop > TREAD_MIN_DEPTH) {
      treadPoints.push(
        treadHeight,
        treadDepthActual * treadCount + leftOverAtBottom,
        treadHeight,
        treadDepthActual * treadCount + leftOverAtTop - leftOverAtBottom,
      );
    }

    if (stairTreadAsLanding) {
      const stairSurface = makeStairSurface({
        outline: stairTreadAsLanding,
        pX,
        pY,
        backRayX,
        backRayY,
        noseRayX,
        noseRayY,
        surfaceType: SurfaceType.LANDING_FROM_TREAD,
        baseElevation: treadHeight,
        nosing,
        construction,
      });

      stairSurfaces.push(stairSurface);
    }
  }

  stairExtrusions.push(
    ...stringers(outline, startEdgeIndex, stringerPlane, treadPoints, bottomOfRun, construction),
    ...fullWidthExtrusions(
      outline,
      startEdgeIndex,
      stringerPlane,
      fullWidthProfiles(treadPoints, bottomOfRun, construction),
    ),
  );

  return {
    type: "STAIR_RUN_V2",
    stairSurfaces,
    stairExtrusions,
    planes: {
      [StairPlanes.BACK]: backEdgesPlane,
      ...stairBlockPlanes(
        bottomOfRun,
        stringerPlane,
        makeStringerProfile({
          treadPoints,
          stringerMinDepth: construction?.hasOwnProperty("stringerMinDepth")
            ? construction["stringerMinDepth"]
            : undefined,
          runBottomX: bottomOfRun,
          riserShelf: !!construction?.hasRisers,
        }),
        construction,
      ),
    },
  };
}

/**
 * generate planes that are usable by other entities to snap to stair
 * bounds
 */
export function stairBlockPlanes(
  bottomOfRun: number,
  stringerPlane: Plane | undefined,
  { backSlopeLower, backSlopeUpper }: StringerProfile,
  construction: StairConstruction | undefined,
): { [key in StairPlanes]?: Plane } {
  const planes: { [key in StairPlanes]?: Plane } = {};

  planes[StairPlanes.BOTTOM] = makeXYPlane(0, 0, bottomOfRun);
  if (!!stringerPlane) {
    planes[StairPlanes.STRINGER] = stringerPlane;
  }

  if (
    construction?.type === StairConstructionType.MONOLITHIC &&
    construction.stairBottomShape === StairBottomShape.FLAT
  ) {
    return planes;
  }

  assertIsNotNullOrUndefined(stringerPlane);
  const [planeNormX, planeNormY, planeNormZ] = planeNormal(stringerPlane);

  if (!(Math.abs(planeNormZ) < DIST_EPSILON)) {
    console.assert(false, "stringer plane normal Z component should be negligible");
  }

  if (!backSlopeLower || !backSlopeUpper) {
    return planes;
  }

  const [lowX, lowY] = backSlopeLower;
  const [upX, upY] = backSlopeUpper;

  // evaluate points at the stringer plane
  const topPoint: Vec3 = [...planeEval(stringerPlane, upX, upY)];
  const bottomLeft: Vec3 = [...planeEval(stringerPlane, lowX, lowY)];
  const bottomRight: Vec3 = [bottomLeft[0] + planeNormX, bottomLeft[1] + planeNormY, bottomLeft[2]];

  const undersidePlane = planeByThreePoints(
    bottomLeft[0],
    bottomLeft[1],
    bottomLeft[2],
    topPoint[0],
    topPoint[1],
    topPoint[2],
    bottomRight[0],
    bottomRight[1],
    bottomRight[2],
  );

  if (undersidePlane) {
    planes[StairPlanes.UNDERSIDE] = undersidePlane;
  }

  return planes;
}

/**
 * create extrusions that are the same width as the stair run
 */
function fullWidthExtrusions(
  outline: PolyLine2,
  edgeIndex: number,
  plane: Plane | undefined,
  profiles: StairProfile[],
): StairExtrusion[] {
  const startEdge = polyLine2Edge(outline, edgeIndex);
  const [startX, startY, endX, endY] = startEdge;
  assertIsNotNullOrUndefined(plane);
  const [nX, nY, nZ] = planeNormal(plane);

  if (polyLine2IsRectangle(outline)) {
    const dist = line2Len(startX, startY, endX, endY);
    const [dX, dY, dZ] = vec3Scale(nX, nY, nZ, dist);
    return profiles.map(({ profile, material }) => ({
      extrusion: extrudeFace(faceNew([profile], plane), -dX, -dY, -dZ),
      material,
    }));
  } else {
    // polygonal extrusion intersection not implemented
    return [];
  }
}
